import { useEffect, useMemo } from 'react';
import { createModuleStrings } from '@platform/core/lib/moduleStrings';
import { moduleBundle } from '@platform/i18n';
import { useModuleStrings } from '@platform/modularity/useModuleStrings';
import type { IFieldDefinition, IRecordTypeDefinition } from '@platform/core/types';
import { fitLabelOf } from '../fitLabelOf';
import { fitBudget, fitColumns, gridColumn, gridMinWidth, gridTemplate } from './gridLayout';
import type { IGridLayout } from './IGridLayout';
import { shownColumns } from './shownColumns';
import { useElementWidth } from './useElementWidth';

/** Ширина контейнера до першого вимірювання: основна область при 1440px без панелей (1440 − меню 248 − відступи 56). */
const INITIAL_WIDTH = 1136;

/**
 * Розкладка сітки записів (прототип v4): стовпці полів `fields` із підписами модуля (ширина — від перейменованого підпису, fitLabelOf);
 * стовпці за замовчуванням (`fixed` = false — не задані збереженим поданням) підбираються за шириною основної області (fitBudget ·
 * fitColumns, виконавець першим), стовпці збереженого подання — усі й у його порядку. `ref` — контейнер подання на всю ширину основної
 * області (сітка таблиці, сітка карток). Показані стовпці за замовчуванням запам'ятовуються для «Редагувати подання» (shownColumns).
 */
export function useGridLayout(rt: IRecordTypeDefinition, fields: readonly IFieldDefinition[], fixed: boolean, lead = true): IGridLayout {
  const strings = useModuleStrings();
  const [ref, width] = useElementWidth<HTMLDivElement>(INITIAL_WIDTH);
  const all = useMemo(() => {
    const { moduleKey, lang } = strings;
    const bundle = createModuleStrings(moduleKey, lang, () => moduleBundle(lang, moduleKey));
    return fields.map((f) => {
      const label = strings.field(rt.key, f.key, f.label);
      return gridColumn(f, label, fitLabelOf(f, bundle.field(rt.key, f.key, f.label), lang, label));
    });
  }, [fields, strings, rt.key]);
  const available = fitBudget(width);
  const columns = useMemo(() => (fixed ? all : fitColumns(all, available)), [all, fixed, available]);
  useEffect(() => { if (!fixed) shownColumns.set(rt.key, columns.map((c) => c.field.key)); }, [rt.key, columns, fixed]);
  return { columns, hidden: all.length - columns.length, template: gridTemplate(columns, lead), minWidth: gridMinWidth(columns, lead), ref };
}
